import { useFocusEffect, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import { ChevronLeft, Scale, Trophy } from 'lucide-react-native';
import { useCallback, useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import type { QuoteRow } from '@/db/types';
import { QUOTE_STATUS_META, groupQuotesByProject, listQuotes } from '@/lib/quotes';
import { goBackOrTo } from '@/lib/nav';
import { getActiveProperty } from '@/lib/tasks';

/**
 * Compare bids (Wave 61) — dedicated side-by-side view of every
 * project that has 2+ quotes. Each project renders its bids as columns
 * with the delta vs the lowest bid, so "who's cheapest and by how
 * much" is one glance, not mental math across list rows.
 */
export default function CompareQuotes() {
  const router = useRouter();
  const db = useSQLiteContext();
  const [items, setItems] = useState<QuoteRow[]>([]);

  const fetch = useCallback(async () => {
    const prop = await getActiveProperty(db);
    if (!prop) return;
    setItems(await listQuotes(db, prop.id));
  }, [db]);

  useFocusEffect(
    useCallback(() => {
      fetch();
    }, [fetch])
  );

  const multiGroups = useMemo(
    () => groupQuotesByProject(items).filter((g) => g.project && g.quotes.length > 1),
    [items]
  );

  const money = (cents: number) => `$${(cents / 100).toLocaleString()}`;

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable
          onPress={() => goBackOrTo(router, '/quotes')}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Compare bids</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 80 }} showsVerticalScrollIndicator={false}>
        {multiGroups.length === 0 ? (
          <View style={styles.empty}>
            <View style={styles.emptyIcon}>
              <Scale size={28} color={Palette.honeyDeep} strokeWidth={1.7} />
            </View>
            <Text style={styles.emptyTitle}>No competing bids yet</Text>
            <Text style={styles.emptyText}>
              Save two or more quotes with the same Project name — &ldquo;Roof
              replacement&rdquo;, &ldquo;New furnace&rdquo; — and they&apos;ll
              line up here side by side with the price spread worked out.
            </Text>
          </View>
        ) : (
          multiGroups.map((g) => {
            const lowest = g.quotes[0];
            return (
              <View key={g.project} style={styles.group}>
                <View style={styles.groupHead}>
                  <Text style={styles.groupTitle} numberOfLines={1}>{g.project}</Text>
                  <Text style={styles.groupSpread}>
                    spread {money(g.maxCents - g.minCents)}
                  </Text>
                </View>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.bidRow}>
                  {g.quotes.map((q, i) => {
                    const meta = QUOTE_STATUS_META[q.status];
                    const delta = q.amount_cents - lowest.amount_cents;
                    const isLow = i === 0;
                    return (
                      <Pressable
                        key={q.id}
                        onPress={() => router.push(`/quotes/${q.id}` as never)}
                        style={({ pressed }) => [
                          styles.bidCard,
                          isLow && styles.bidCardLow,
                          pressed && { opacity: 0.9 },
                        ]}>
                        {isLow ? (
                          <View style={styles.lowRibbon}>
                            <Trophy size={11} color={Palette.cream} strokeWidth={2.2} />
                            <Text style={styles.lowRibbonText}>LOWEST</Text>
                          </View>
                        ) : (
                          <Text style={styles.deltaText}>+{money(delta)}</Text>
                        )}
                        <Text style={styles.bidAmount}>{money(q.amount_cents)}</Text>
                        <Text style={styles.bidVendor} numberOfLines={2}>
                          {q.vendor ?? 'Unknown vendor'}
                        </Text>
                        <Text style={styles.bidDate}>
                          {new Date(q.quoted_on).toLocaleDateString(undefined, {
                            month: 'short', day: 'numeric', year: 'numeric',
                          })}
                        </Text>
                        <View style={[styles.statusPill, { borderColor: meta.tint }]}>
                          <Text style={[styles.statusPillText, { color: meta.tint }]}>
                            {meta.label}
                          </Text>
                        </View>
                      </Pressable>
                    );
                  })}
                </ScrollView>
              </View>
            );
          })
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  group: {
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
  },
  groupHead: {
    flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between',
    marginBottom: 8, gap: 10,
  },
  groupTitle: {
    flex: 1,
    fontFamily: Fonts.serifBold, fontSize: 17, color: Palette.ink,
    letterSpacing: -0.2,
  },
  groupSpread: {
    fontFamily: Fonts.sansBold, fontSize: 12.5, color: Palette.honeyDeep,
  },
  bidRow: { gap: 10, paddingBottom: 4 },
  bidCard: {
    width: 150,
    backgroundColor: Palette.card,
    borderWidth: 1.5, borderColor: Palette.line,
    borderRadius: Radius.xl,
    padding: 12,
    gap: 4,
  },
  bidCardLow: { borderColor: Palette.forest, backgroundColor: '#EEF3EE' },
  lowRibbon: {
    alignSelf: 'flex-start',
    flexDirection: 'row', alignItems: 'center', gap: 4,
    backgroundColor: Palette.forest,
    paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999,
  },
  lowRibbonText: {
    fontFamily: Fonts.sansBold, fontSize: 10.5, color: Palette.cream,
    letterSpacing: 0.5,
  },
  deltaText: {
    fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.clay,
    paddingVertical: 3,
  },
  bidAmount: {
    fontFamily: Fonts.serifBold, fontSize: 22, color: Palette.forest,
    letterSpacing: -0.5,
  },
  bidVendor: {
    fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink,
    lineHeight: 16,
  },
  bidDate: {
    fontFamily: Fonts.sansMedium, fontSize: 12, color: Palette.inkSoft,
  },
  statusPill: {
    alignSelf: 'flex-start',
    marginTop: 2,
    paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999,
    borderWidth: 1.2,
  },
  statusPillText: { fontFamily: Fonts.sansBold, fontSize: 10 },
  empty: {
    alignItems: 'center', paddingHorizontal: 32, paddingVertical: 60, gap: 12,
  },
  emptyIcon: {
    width: 72, height: 72, borderRadius: 36,
    backgroundColor: 'rgba(225,163,61,0.18)',
    alignItems: 'center', justifyContent: 'center',
  },
  emptyTitle: { fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.ink },
  emptyText: {
    fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkSoft,
    textAlign: 'center', lineHeight: 19,
  },
});
